import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {Api} from "../../../../../../common/services/api.service";
import {Base} from "../../../../../../common/interfaces/base.interfaces";
import {Table2Component} from "src/modules/common/components/table2/table.component";
import {Table} from "src/modules/common/interfaces/table.interface";
import {ModalService} from "src/modules/section/services/modal.service";
import {ModalComponent} from "src/modules/common/components/modal/modal.component";
import {FormControl} from "@angular/forms";
import {takeUntil} from "rxjs/operators";
import {DomSanitizer} from "@angular/platform-browser";
import * as moment from "moment-timezone";
import {SpinnerService} from "../../../../../../common/services/spinner.service";
import {Api3Service} from "../../../../../../common/services/api3.service";


@Component({
    selector: "email-logs-list",
    template: `
        <common-confirm></common-confirm>
        <mat-card>
            <mat-card-content>
                <common-table2 *ngIf="listTable" [settings]="listTable">
                    <div row1>
                        <common-form-date-range label="Sending" (valueChange)="dateRange.setValue($event)">
                        </common-form-date-range>
                    </div>
                </common-table2>
            </mat-card-content>
        </mat-card>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailLogsListComponent implements Base.IComponent, OnInit, OnDestroy {

    private destroy$: EventEmitter<boolean> = new EventEmitter(false);

    private users: any;

    @ViewChild(Table2Component, {static: false})
    public tableRef: Table2Component;

    public listTable: Table.ISettings;

    public readonly state: Base.IState;

    public dateRange: FormControl = new FormControl(null);

    public constructor(
        private spinnerService: SpinnerService,
        private modalService: ModalService,
        private sanitizer: DomSanitizer,
        private changeDetectorRef: ChangeDetectorRef,
        private api3Service: Api3Service,
    ) {
    }

    private get url(): { url: string[], version: number, query?: { [key: string]: any } } {
        return {
            url: ["admin", "logs"],
            version: 3,
            query: {
                message: "Email sent",
                date_range: this.dateRange.value
            }
        };
    }

    private showEmails(data: any): string {

        if (!data || !data.length) {
            return "";
        }

        const list: string[] = Object.values(data);
        if (list.length > 1) {
            return `<b>${list[0]}</b> + ${list.length - 1}`;
        }
        return list[0];
    }

    public showList(title: string, data: any): void {

        if (!data || !data.length) {
            return;
        }

        const list: string[] = Object.values(data);
        if (list.length > 1) {
            this.modalService.open(ModalComponent, {
                title,
                template: list.join("<br>")
            });
        }
    }

    private prepareList(): void {
        const actions: Table.IAction[] = [
            {
                name: "view",
                title: "View",
                click: async (row: any): Promise<void> => {
                    await this.modalService.open(ModalComponent, {
                        title: row.context?.subject,
                        template: this.sanitizer.bypassSecurityTrustHtml(row.context?.body || ""),
                        modalWidth: "calc(100% - 250px)"
                    });
                }
            }
        ];

        const columns: Table.ICol[] = [
            {
                data: "subject",
                title: "Mail Subject",
                sortable: false,
                searchable: true,
                render: (row: any): string => {
                    return row.context?.subject || "";
                }
            },
            {
                data: "to",
                title: "Sent to",
                render: (row: any): string => {
                    return this.showEmails(row.context?.to);
                },
                click: (row: any): void => {
                    this.showList(row.context?.subject + ". Sent to", row.context?.to);
                },
                sortable: false,
                searchable: false
            },
            {
                data: "cc",
                title: "Sent cc",
                render: (row: any): string => {
                    return this.showEmails(row.context?.cc);
                },
                click: (row: any): void => {
                    this.showList(row.context?.subject + ". Sent cc", row.context?.cc);
                },
                sortable: false,
                searchable: false,
            },
            {
                data: "bcc",
                title: "Sent bcc",
                render: (row: any): string => {
                    return this.showEmails(row.context?.bcc);
                },
                click: (row: any): void => {
                    this.showList(row.context?.subject + ". Sent bcc", row.context?.bcc);
                },
                sortable: false,
                searchable: false
            },
            {
                data: "created_at",
                title: "Date of sending",
                searchable: false,
                sortable: false,
                render: (row: any): string => {
                    return row.timestamp
                        ? moment.utc(row.timestamp).tz("Asia/Jerusalem").format("YYYY-MM-DD HH:mm:ss")
                        : "";
                }
            },
            {
                data: "user_id",
                title: "User",
                sortable: false,
                searchable: false,
                render: (row: any): string => {
                    const id = row.context?.user_id ?? 0;

                    if (id > 0 && this.users.hasOwnProperty(id)) {
                        return this.users[id];
                    }

                    return "";
                }
            }
        ];

        this.listTable = {
            actions,
            api: this.url,
            columns,
            search_in: {
                "Subject": "context.subject"
            },
            search_default: ["context.subject"],
            sort_default: {
                data: "created_at",
                dir: "desc"
            }
        };
        this.changeDetectorRef.markForCheck();
    }

    private async getUsers(): Promise<any> {
        this.spinnerService.show();

        const {data}: Api.IResponse = await this.api3Service.get(`/admin/users`, {
            data_structure: "select",
        });

        this.spinnerService.hide();

        if (data) {

            this.users = [];

            for (const entry of data) {
                this.users[entry.value] = entry.name;
            }
        }
    }

    public ngOnInit(): void {
        this.getUsers().then(() => {
            this.prepareList();
        });

        this.dateRange.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((): void => {
            this.tableRef.reload(this.url);
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public ngConfig(): Base.IConfig {
        return {
            name: "email-logs",
            actions: {
                "browse": "*"
            }
        };
    }
}
